<template>
	<datatable
		size="large"
		:headers
		:value="computedItems"
		:filter-options="filterOptions"
		:global-filter-fields="filterFields"
		expandable
		:loading>
		<template #filter-delivery_method="{ model, callback }">
			<app-dropdown
				v-if="model"
				v-model="model.value"
				label-key="value"
				size="small"
				:options="deliveryMethods"
				@update:model-value="callback()">
				Delivery Method
			</app-dropdown>
		</template>

		<template #row-quantity_required="{ row }">
			{{ statusParam(row, 'quantity_required', 'pick') }}
		</template>

		<template #row-quantity_picked="{ row }">
			<div v-if="row.pick_status && row.pick_status.length > 0">
				{{ row.pick_status[0].quantity_confirmed }}
			</div>
		</template>

		<template #row-quantity_loaded="{ row }">
			<quantity-field
				v-if="row.pick_status && row.pick_status.length > 0"
				v-model="row.pick_status[0].quantity_loaded"
				:max="row.pick_status[0].quantity_required"
				:disabled="row.type == 'Kit' || row.type == 'Comprises Of'"
				@update:model-value="emit('updatedItems', row)" />
		</template>

		<template #row-weight="{ row }">
			{{ row.pick_status[0].weight }}
		</template>

		<template #row-delivery_method="{ row }">
			<div class="font-semibold">
				{{ statusParam(row, 'delivery_method', 'pick') }}
			</div>
			<div>
				{{ statusParam(row, 'delivery_method_name', 'pick') }}
			</div>
		</template>

		<template #row-notes="{ row }">
			<notes-button
				v-if="row.pick_status && row.pick_status[0]"
				tablet
				:status="row.pick_status[0]"
				@click="editItem(row)" />
		</template>
	</datatable>

	<edit-contract-item-note
		:item="editing"
		area="pick"
		@updated="emit('updatedNote', editing)"
		@closed="editing = undefined" />
</template>

<script lang="ts" setup>
	import type { Header } from '@/data/types/datatable'
	import { computed, ref, watch } from 'vue'
	import NotesButton from '@/components/partials/cat2/NotesButton.vue'
	import QuantityField from '@/components/partials/tables/QuantityField.vue'
	import { ContractItem } from '@/data/schema/contract-item'
	import useContracts, { statusParam } from '@/composables/contracts'
	import { ContractItemStatus } from '@/data/schema/contract-item-status'
	import EditContractItemNote from '@/components/partials/cat2/EditContractItemNote.vue'
	import { FilterMatchMode } from '@primevue/core/api'
	const editing = ref<ContractItem | undefined>(undefined)

	const props = defineProps({
		items: {
			type: Array<ContractItem>,
			default: []
		},
		loading: Boolean
	})

	const headers: Header[] = [
		{ value: 'item_number', text: 'Item No', props: { sortable: true, width: 120 } },
		{ value: 'description', text: 'Item Description', props: { width: 250 } },
		{ value: 'type', text: 'Type', props: { sortable: true, width: 120 } },
		{ value: 'quantity_required', text: 'REQ', props: { width: 50 } },
		{ value: 'quantity_picked', text: 'PCK', props: { width: 50 } },
		{ value: 'quantity_loaded', text: 'LOAD', props: { width: 200 } },
		{ value: 'delivery_method', text: 'DEL', props: { width: 150, showFilterMenu: false } },
		{ value: 'weight', text: 'Total Weight', props: { sortable: true } },
		{ value: 'notes', text: 'Notes' }
	]

	// Filtering and options
	const { getDeliveryMethodOptions } = useContracts()
	const deliveryMethods = ref([])

	const filterOptions = ref({
		delivery_method: { value: null, matchMode: FilterMatchMode.EQUALS }
	})

	const filterFields = computed(() => {
		let fields = []

		if (deliveryMethods.value.length > 0) {
			fields.push('delivery_method')
		}

		return fields
	})

	// Move data to top layer for client side filtering
	const computedItems = computed(() => {
		return props.items.map((item) => ({
			...item,
			delivery_method: item.pick_status?.[0]?.delivery_method ?? null
		}))
	})

	function editItem(item: ContractItem) {
		if (!item.pick_status || !item.pick_status[0]) {
			item.pick_status = []
			item.pick_status[0] = new ContractItemStatus()
		}

		if (!item.pick_status[0].notes) {
			item.pick_status[0].notes = ''
		}

		editing.value = item
	}

	const emit = defineEmits(['updatedItems', 'updatedNote'])

	watch(
		() => props.loading,
		(newValue) => {
			if (!newValue) {
				deliveryMethods.value = getDeliveryMethodOptions(props.items, 'pick')
			}
		}
	)
</script>
